import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Download } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function POPs() {
  const [pops, setPops] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'eft', reference_number: '', bank_account: '',
    linked_invoice_id: '', linked_po_id: '', linked_supplier_id: '', linked_client_id: '',
    notes: '', file: null
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [popsRes, invRes, poRes, supRes, cliRes] = await Promise.all([
        axios.get(`${API}/pops`, { withCredentials: true }),
        axios.get(`${API}/invoices`, { withCredentials: true }),
        axios.get(`${API}/purchase-orders`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/crm/companies`, { withCredentials: true })
      ]);
      setPops(popsRes.data);
      setInvoices(invRes.data);
      setPurchaseOrders(poRes.data);
      setSuppliers(supRes.data);
      setClients(cliRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const data = new FormData();
      data.append('amount', formData.amount);
      data.append('payment_date', formData.payment_date);
      data.append('payment_method', formData.payment_method);
      data.append('reference_number', formData.reference_number);
      data.append('bank_account', formData.bank_account);
      if (formData.linked_invoice_id) data.append('linked_invoice_id', formData.linked_invoice_id);
      if (formData.linked_po_id) data.append('linked_po_id', formData.linked_po_id);
      if (formData.linked_supplier_id) data.append('linked_supplier_id', formData.linked_supplier_id);
      if (formData.linked_client_id) data.append('linked_client_id', formData.linked_client_id);
      data.append('notes', formData.notes);
      if (formData.file) data.append('file', formData.file);

      await axios.post(`${API}/pops`, data, { withCredentials: true });
      toast.success('Proof of Payment uploaded');
      setShowDialog(false);
      setFormData({
        amount: '', payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'eft', reference_number: '', bank_account: '',
        linked_invoice_id: '', linked_po_id: '', linked_supplier_id: '', linked_client_id: '',
        notes: '', file: null
      });
      loadData();
    } catch (error) {
      toast.error('Failed to upload POP');
    } finally {
      setUploading(false);
    }
  };

  const downloadPOP = async (pop) => {
    try {
      const { data } = await axios.get(`${API}/pops/${pop.id}/download`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', pop.original_filename || 'pop.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;

  return (
    <div className="p-8" data-testid="pops-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="pops-title">Proof of Payment</h1>
          <p className="mt-2 text-zinc-600">Manage payment confirmations and receipts</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-pop-button">
              <Plus size={18} /> Upload POP
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Upload Proof of Payment</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Amount</Label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({...formData, amount: e.target.value})} required data-testid="pop-amount-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Payment Date</Label>
                  <Input type="date" value={formData.payment_date} onChange={(e) => setFormData({...formData, payment_date: e.target.value})} required data-testid="pop-date-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Payment Method</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({...formData, payment_method: value})}>
                    <SelectTrigger className="mt-2" data-testid="pop-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="eft">EFT</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Reference Number</Label>
                  <Input value={formData.reference_number} onChange={(e) => setFormData({...formData, reference_number: e.target.value})} data-testid="pop-ref-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Bank Account</Label>
                <Input value={formData.bank_account} onChange={(e) => setFormData({...formData, bank_account: e.target.value})} data-testid="pop-bank-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Link to Invoice</Label>
                  <Select value={formData.linked_invoice_id} onValueChange={(value) => setFormData({...formData, linked_invoice_id: value})}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {invoices.map(i => <SelectItem key={i.id} value={i.id}>{i.invoice_number}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Link to PO</Label>
                  <Select value={formData.linked_po_id} onValueChange={(value) => setFormData({...formData, linked_po_id: value})}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {purchaseOrders.map(p => <SelectItem key={p.id} value={p.id}>{p.po_number}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Link to Supplier</Label>
                  <Select value={formData.linked_supplier_id} onValueChange={(value) => setFormData({...formData, linked_supplier_id: value})}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Link to Client</Label>
                  <Select value={formData.linked_client_id} onValueChange={(value) => setFormData({...formData, linked_client_id: value})}>
                    <SelectTrigger className="mt-2"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Upload File</Label>
                <Input type="file" onChange={(e) => setFormData({...formData, file: e.target.files[0]})} data-testid="pop-file-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-zinc-600">Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={2} data-testid="pop-notes-input" className="mt-2 border-zinc-300 hover:border-black transition-colors" />
              </div>
              <Button type="submit" disabled={uploading} className="w-full bg-black text-white hover:bg-zinc-800" data-testid="submit-pop-button">
                {uploading ? 'Uploading...' : 'Upload POP'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="pops-table">
            <thead className="border-b border-zinc-200 bg-zinc-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Method</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Reference</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-zinc-600">Linked To</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-zinc-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {pops.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-zinc-500" data-testid="no-pops">No proofs of payment yet.</td></tr>
              ) : pops.map((pop) => (
                <tr key={pop.id} className="hover:bg-zinc-50 transition-colors" data-testid={`pop-row-${pop.id}`}>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{new Date(pop.payment_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 font-semibold text-zinc-900">R {pop.amount.toLocaleString()}</td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{pop.payment_method}</td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">{pop.reference_number || 'N/A'}</td>
                  <td className="px-6 py-4 text-zinc-600 text-sm">
                    {pop.linked_invoice_id && <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs mr-1">Invoice</span>}
                    {pop.linked_po_id && <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs mr-1">PO</span>}
                    {pop.linked_supplier_id && <span className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs mr-1">Supplier</span>}
                    {pop.linked_client_id && <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs">Client</span>}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {pop.storage_path && (
                      <button onClick={() => downloadPOP(pop)} className="text-blue-600 hover:text-blue-800" data-testid={`download-pop-${pop.id}`}>
                        <Download size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
